import { Button } from "components/Button/Button";
import { NumberFormComponent } from "components/Form/components/Number/NumberFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType } from "config/config";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { useCallback } from "react";
import { Field } from "react-final-form";
import styles from "./ResourceDetails.module.scss";

export const useMaxRuntimeMinutes = () => {
    const workspaceMaxRuntimeMinutesValue = useWorkspaceConfigValue(
        "EXECUTION_MAX_RUNTIME_MINUTES"
    );

    const getMaxRuntimeMinutesInitialValue = useCallback(
        (formValue) => formValue ?? workspaceMaxRuntimeMinutesValue,
        [workspaceMaxRuntimeMinutesValue]
    );
    const getMaxRuntimeMinutesSubmitValue = useCallback(
        (formValue) => (formValue === workspaceMaxRuntimeMinutesValue ? null : Number(formValue)),
        [workspaceMaxRuntimeMinutesValue]
    );

    return {
        getMaxRuntimeMinutesInitialValue,
        getMaxRuntimeMinutesSubmitValue,
        workspaceMaxRuntimeMinutesValue,
    };
};

export const MaxRuntimeMinutesField = ({
    label,
    workspaceMaxRuntimeMinutesValue,
    recordType,
    handleSubmit,
    isDisabled,
    initialValue,
}: {
    label?: string;
    workspaceMaxRuntimeMinutesValue: number;
    recordType: FeConfigType;
    handleSubmit?: () => any;
    isDisabled?: boolean;
    initialValue?: any;
}) => (
    <Field name="max_runtime_minutes" initialValue={initialValue}>
        {({ input, meta }) => {
            const value = input.value;
            const setWorkspaceValue = () => {
                input.onChange(workspaceMaxRuntimeMinutesValue);
                handleSubmit?.();
            };

            const actionButton = (
                <ResetToWorkspaceValueButton
                    value={value}
                    workspaceValue={workspaceMaxRuntimeMinutesValue}
                    onClick={setWorkspaceValue}
                    isSetToWorkspaceAllowed={recordType !== "execution"}
                />
            );
            return (
                <>
                    <NumberFormComponent
                        input={{
                            ...input,
                            onBlur: () => {
                                if (!meta.pristine) {
                                    if (value == null || value === "") {
                                        setWorkspaceValue();
                                    } else if (value < 0) {
                                        input.onChange(Math.abs(value));
                                    }
                                    handleSubmit?.();
                                }
                            },
                            onKeyDown: (event) => {
                                if (
                                    event.key === "Backspace" ||
                                    event.key === "Delete" ||
                                    event.key === "Tab" ||
                                    event.key === "Escape" ||
                                    event.key === "Enter" ||
                                    event.key === "ArrowLeft" ||
                                    event.key === "ArrowRight" ||
                                    event.key === "ArrowUp" ||
                                    event.key === "ArrowDown"
                                ) {
                                    return;
                                }
                                if (!/[0-9]/.test(event.key)) {
                                    event.preventDefault();
                                }
                            },
                        }}
                        fieldProps={{
                            label,
                            docs: {
                                urlPath: "/docs/executions#max_runtime_minutes",
                            },
                            actionButtons: actionButton,
                        }}
                        fieldAttr={{ min: "0", disabled: isDisabled }}
                        meta={meta}
                    />
                </>
            );
        }}
    </Field>
);

const ResetToWorkspaceValueButton = ({
    value,
    isSetToWorkspaceAllowed,
    workspaceValue,
    onClick,
}: {
    workspaceValue: number;
    isSetToWorkspaceAllowed?: boolean;
    value?: number;
    onClick: () => void;
}) => {
    const isWorkspaceValueApplied = value === workspaceValue;
    const overlay = isWorkspaceValueApplied
        ? "workspace setting value is applied"
        : `reset to workspace setting value ${workspaceValue}`;

    return isSetToWorkspaceAllowed ? (
        <Tooltip
            overlay={overlay}
            align={{ offset: [0, 15] }}
            overlayClassName={`light ${styles.actionButtonTooltip}`}
        >
            <span className={styles.actionButton}>
                <Button
                    icon={IconCollection.refresh}
                    onClick={onClick}
                    disabled={isWorkspaceValueApplied}
                    variation={"white"}
                />
            </span>
        </Tooltip>
    ) : null;
};
